/**
 * MCPSection.tsx
 * Section "Votre IA, alimentée par 1M+ décisions françaises"
 * Colonne droite : grille logos clients MCP (plus de JSON config)
 */

import { useTranslation } from 'react-i18next';
import { ClaudeIcon, ChatGPTIcon, MistralIcon, GeminiIcon } from './MCPBadge';

const BULLETS = ['citations', 'synthesis', 'setup', 'included'] as const;

const LOGO_CLIENTS = [
  {
    id:     'claude',
    name:   'Claude',
    sub:    'Anthropic',
    Icon:   ClaudeIcon,
    color:  '#D97757',
    bg:     'rgba(217,119,86,0.12)',
    border: 'rgba(217,119,86,0.25)',
  },
  {
    id:     'chatgpt',
    name:   'ChatGPT',
    sub:    'OpenAI',
    Icon:   ChatGPTIcon,
    color:  '#10a37f',
    bg:     'rgba(16,163,127,0.12)',
    border: 'rgba(16,163,127,0.25)',
  },
  {
    id:     'mistral',
    name:   'Le Chat',
    sub:    'Mistral',
    Icon:   MistralIcon,
    color:  '#FF7000',
    bg:     'rgba(255,112,0,0.12)',
    border: 'rgba(255,112,0,0.25)',
  },
  {
    id:     'gemini',
    name:   'Gemini',
    sub:    'Google',
    Icon:   GeminiIcon,
    color:  '#4285f4',
    bg:     'rgba(66,133,244,0.12)',
    border: 'rgba(66,133,244,0.25)',
  },
];

export const MCPSection = () => {
  const { t } = useTranslation();
  return (
  <section
    className="py-20 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #1c2333 0%, #252e44 55%, #2d2060 100%)' }}
  >
    {/* Watermark ⚖ */}
    <span
      className="absolute right-[-60px] bottom-[-60px] pointer-events-none select-none leading-none text-white"
      style={{ fontSize: '280px', opacity: 0.06 }}
      aria-hidden="true"
    >
      ⚖
    </span>

    <div className="max-w-[1100px] mx-auto px-6 sm:px-8 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">

        {/* ── Colonne gauche : texte ── */}
        <div>
          <span className="block text-[10px] font-bold uppercase tracking-[.14em] text-white/40 mb-2.5">
            {t('mcp.section.eyebrow')}
          </span>
          <h2 className="font-serif text-[30px] sm:text-[34px] font-bold text-white tracking-[-0.03em] leading-[1.15] mb-4">
            {t('mcp.section.title_start')}{' '}
            <span style={{ color: '#79b8ff' }}>{t('mcp.section.title_highlight')}</span>
          </h2>
          <p className="text-[14px] text-white/60 leading-[1.7] mb-7">
            {t('mcp.section.description')}
          </p>

          {/* Bullets */}
          <div className="flex flex-col gap-2.5 mb-8">
            {BULLETS.map((id) => (
              <div key={id} className="flex items-start gap-2.5 text-[13px] text-white/75">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-[5px]"
                  style={{ background: '#79b8ff' }}
                />
                <div>
                  <strong className="text-white font-semibold">{t(`mcp.section.bullets.${id}.title`)}</strong>
                  {t('mcp.section.bullet_separator')}{t(`mcp.section.bullets.${id}.desc`)}
                </div>
              </div>
            ))}
          </div>

          {/* CTA unique */}
          <div className="flex flex-wrap gap-3">
            <a
              href="https://smartlawyer.ai/mcp/"
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-[10px] text-white text-[14px] font-bold transition-opacity hover:opacity-90"
              style={{ background: '#2b5ce6' }}
            >
              {t('mcp.section.cta')}
            </a>
          </div>
        </div>

        {/* ── Colonne droite : logos clients ── */}
        <div
          className="rounded-2xl p-6"
          style={{ background: 'rgba(0,0,0,.4)', border: '1px solid rgba(255,255,255,.1)' }}
        >
          {/* Titre */}
          <p className="text-[11px] font-bold uppercase tracking-[.12em] text-white/30 mb-5">
            {t('mcp.section.compatible')}
          </p>

          {/* Grille 2×2 */}
          <div className="grid grid-cols-2 gap-3">
            {LOGO_CLIENTS.map(({ id, name, sub, Icon, color, bg, border }) => (
              <div
                key={id}
                className="rounded-xl p-4 flex flex-col items-center gap-3"
                style={{ background: bg, border: `1px solid ${border}` }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.07)' }}
                >
                  <Icon className="w-7 h-7" />
                </div>
                <div className="text-center">
                  <div className="text-[13px] font-bold text-white leading-tight">{name}</div>
                  <div className="text-[11px] text-white/40 mt-0.5">{sub}</div>
                </div>
                <div
                  className="w-full h-0.5 rounded-full opacity-40"
                  style={{ background: color }}
                />
              </div>
            ))}
          </div>

          {/* Footer */}
          <p className="mt-5 text-[11px] text-white/25 text-center leading-relaxed">
            {t('mcp.section.also')}
          </p>
        </div>

      </div>
    </div>
  </section>
  );
};
